// AI CRM Component
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import TabNavigation from "../ui/TabNavigation";
import CustomerDetailsModal from "../CustomerDetailsModal";
import DatePicker from "../DatePicker";
import DashboardTab from "./tabs/DashboardTab";
import CRMLeadsTab from "./tabs/CRMLeadsTab";
import CustomersTab from "./tabs/CustomersTab";
import BillingSubscriptionsTab from "./tabs/BillingSubscriptionsTab";
import AuditLogTab from "./tabs/AuditLogTab";
import CreateCustomerModal from "./modals/CreateCustomerModal";
import FollowUpModal from "./modals/FollowUpModal";
import RenewalModal from "./modals/RenewalModal";
import RenewalHistoryModal from "./modals/RenewalHistoryModal";
import EditCustomerModal from "./modals/EditCustomerModal";
import LeadDetailsModal from "./modals/LeadDetailsModal";
import ConvertToCustomerModal from "./modals/ConvertToCustomerModal";
import ApiLoader from "../ui/ApiLoader";
import PageLoader from "../ui/PageLoader";

import {
  fetchEnabledModulesForCompanyApi,
  fetchCategoriesApi,
  fetchSubCategoriesWithTrustCenterApi,
  updateTrustCenterToggleApi,
  fetchCustomerRequirementsApi,
  createCustomerApi,
  updateCustomerApi,
  updateCustomerStatusApi,
  fetchEnabledModulesForSidebarApi,
  fetchDashboardStatsApi,
  fetchCustomersApi,
  fetchAvailableModulesApi,
  fetchAuditLogApi,
  fetchTopCustomersApi,
  fetchEnabledModulesApi,
  updateAdditionalModuleApi,
  fetchBillingDataApi,
  renewCustomerSubscriptionApi,
  getCustomerRenewalHistoryApi,
  fetchSuperAdminModulesApi,
  fetchCompanyModuleUsageApi,
  fetchUsageStatisticsApi
} from "../../services/axios/AuthApis/crmApis";
import AiCRMApis from "../../services/axios/AuthApis/AiCRMApis";
import { saveUserApi, fetchConfigSettingsApi } from "../../services/axios/AuthApis/AdministrationApis";
import { customerConfig } from "../../constants/AppEnums";

// Icons
import {
    UserGroupIcon,
    BuildingOfficeIcon,
    CogIcon,
    ChartBarIcon,
    PlusIcon,
    PencilIcon,
    MapPinIcon,
    TrashIcon,
    EyeIcon,
    ArrowPathIcon,
    FunnelIcon,
    DocumentArrowDownIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    GlobeAltIcon,
    ServerIcon,
    EnvelopeIcon,
    PhoneIcon,
    KeyIcon,
    ShieldCheckIcon,
    BanknotesIcon,
    ChartPieIcon,
    CalendarIcon,
    MagnifyingGlassIcon,
    AdjustmentsHorizontalIcon,
    BellIcon,
    XMarkIcon,
    InformationCircleIcon,
    UserIcon,
    BriefcaseIcon
  } from '@heroicons/react/24/outline';
import toast from "../../utils/notify";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AiCRM = () => {
    const { user, backendUrl } = useAuth();
    const navigate = useNavigate();
    const API_BASE_URL = backendUrl || process.env.REACT_APP_BACKEND_URL || "";
    
    // State Management
    const [activeTab, setActiveTab] = useState('dashboard');
    const [customerActiveTab, setCustomerActiveTab] = useState("Default");
    const [isDarkMode, setIsDarkMode] = useState(document.documentElement.classList.contains('dark'));

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [errors, setErrors] = useState({});
    // Enabled modules for the logged-in customer, loaded on mount below
    // (fetchEnabledModulesApi always resolves to an array, see crmApis.js).
    const [enabledModules, setEnabledModules] = useState([]);
    

    // Theme-aware toast helper
    const showToast = {
      success: (message) => toast.success(message, {
        style: {
          background: isDarkMode ? '#1f2937' : '#ffffff',
          color: isDarkMode ? '#f9fafb' : '#111827',
          border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
        },
        iconTheme: {
          primary: isDarkMode ? '#10b981' : '#059669',
          secondary: isDarkMode ? '#1f2937' : '#ffffff',
        },
      }),
      error: (message) => toast.error(message, {
        style: {
          background: isDarkMode ? '#1f2937' : '#ffffff',
          color: isDarkMode ? '#f9fafb' : '#111827',
          border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
        },
        iconTheme: {
          primary: isDarkMode ? '#ef4444' : '#dc2626',
          secondary: isDarkMode ? '#1f2937' : '#ffffff',
        },
      }),
    };    
    // Data State
    const [dashboardData, setDashboardData] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [customersLoading, setCustomersLoading] = useState(true);
    const [availableModules, setAvailableModules] = useState({});
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [auditLog, setAuditLog] = useState([]);
    const [adminUsers, setAdminUsers] = useState([]);
    const [topCustomers, setTopCustomers] = useState([]);
    const [selectedModules, setSelectedModules] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [usageStats, setUsageStats] = useState(null);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [subSubCategories, setSubSubCategories] = useState([]);
    const [subCategoryToggles, setSubCategoryToggles] = useState({});
    const [billingData, setBillingData] = useState([]);


      useEffect(() => {
    const storedUsers = JSON.parse(localStorage.getItem("admin_users")) || [];
    setAdminUsers(storedUsers);
  }, []);
  

// Filter only customer_admin users
const formattedUsers = adminUsers
  .filter((user) => user.role === "customer_admin")
  .map((user) => ({
    id: user.id,
    sender: `${user.firstname} ${user.lastname}`,
    role: user.role,
    company: user.company,
    status: user.status,
    avatar: `${user.firstname?.[0]?.toUpperCase() || ""}${user.lastname?.[0]?.toUpperCase() || ""}`,
    email: user.email || "",
    phone: user.phone || "",
    location: user.location || "",
  }));  

    // Filters and Search
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
      status: '',
      subscription_plan: '',
      industry: '',
      region: '',
      organization_size: ''
    });
    // Applied filters – only updated when user clicks "Apply Filter"
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const [appliedFilters, setAppliedFilters] = useState({
      status: '',
      subscription_plan: '',
      industry: '',
      region: '',
      organization_size: ''
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(25);
    
    // Customer pagination state
    const [customerPage, setCustomerPage] = useState(1);
    const [customerLimit] = useState(20);
    const [customerTotal, setCustomerTotal] = useState(0);
    const [hasMoreCustomers, setHasMoreCustomers] = useState(true);
    const [loadingMoreCustomers, setLoadingMoreCustomers] = useState(false);
    
    // Modal States
    const [showCreateCustomerModal, setShowCreateCustomerModal] = useState(false);
    const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
    const [showModuleAssignmentModal, setShowModuleAssignmentModal] = useState(false);
    const [showStatusUpdateModal, setShowStatusUpdateModal] = useState(false);
    const [showBulkOperationModal, setShowBulkOperationModal] = useState(false);
    const [showRenewalModal, setShowRenewalModal] = useState(false);
    const [renewalCustomer, setRenewalCustomer] = useState(null);
    
    // Selected Items for Bulk Operations
    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [showViewDetailsModal, setShowViewDetailsModal] = useState(false);
    const [AIAssestManagerModules, setAIAssestManagerModules] = useState({});
    const [trustCenterLoading, setTrustCenterLoading] = useState(false);


    

    const [modules, setModules] = useState({
      Dashboard: true,
      "Trust Center": true,
      "Risk Management": false,
      "Team Collaboration": true,
      "Document Library": true,
      "AI Analytics": true,
      "Incident Reporting": false,
      "Security Dashboard": true,
      "AI-LMS":true,
      "AI Asset Manager":true
    });

    const [trustCenterModules, setTrustCenterModules] = useState({

    });

    const [additionalModules, setAdditionalModules] = useState({});



    
    // Form Data - CRM Lead
    const [customerFormData, setCustomerFormData] = useState({
      company_name: '',
      industry: 'technology',
      company_size: null,
      price: '',
      primary_contact_name: '',
      primary_contact_email: '',
      primary_contact_phone: '',
      primary_contact_title: '',
      lead_source: '',
      status: 'New Lead',
      notes: '',
      tags: [],
      conversion_probability: 0
    });

    // CRM Leads State
    const [crmLeads, setCrmLeads] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [leadFilters, setLeadFilters] = useState({ status: "", industry: "", company_name: "", contact: "", from_date: "", to_date: "" });
    const [showFollowUpModal, setShowFollowUpModal] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);
    const [showLeadDetailsModal, setShowLeadDetailsModal] = useState(false);
    const [showConvertToCustomerModal, setShowConvertToCustomerModal] = useState(false);
    const [editingFollowUpIndex, setEditingFollowUpIndex] = useState(null);
    const [followUpFormData, setFollowUpFormData] = useState({
      scheduled_date: '',
      follow_up_type: 'Call',
      notes: '',
      assigned_to: '',
      outcome: '',
      next_action: '',
      status: 'Scheduled'
    });
    const [customerAdminFormData, setCustomerAdminFormData] = useState({
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      customername: '',
      location: '',
      department: '',
      employeecount: '',
      price: '',
      logoImg: null
    });

    // clear convert modal state when visibility changes
    React.useEffect(() => {
      if (!showConvertToCustomerModal) {
        setErrors({});
        setCustomerAdminFormData(prev => ({
          ...prev,
          logoImg: null
        }));
      }
    }, [showConvertToCustomerModal]);
    const [employeeConfig, setEmployeeConfig] = useState([]);
    const [renewalFormData, setRenewalFormData] = useState({
      employeecount: '',
      price: '',
      renewal_date: ''
    });
    const [renewalHistory, setRenewalHistory] = useState([]);
    const [showRenewalHistoryModal, setShowRenewalHistoryModal] = useState(false);
    const [selectedCustomerForHistory, setSelectedCustomerForHistory] = useState(null);
    
    // Audit log pagination state
    const [auditLogPage, setAuditLogPage] = useState(1);
    const [auditLogLimit] = useState(10);
    const [auditLogTotal, setAuditLogTotal] = useState(0);
    const [hasMoreAuditLogs, setHasMoreAuditLogs] = useState(true);
    const [loadingMoreAuditLogs, setLoadingMoreAuditLogs] = useState(false);

    // Tab Configuration - Enhanced with more comprehensive features
    const tabs = [
      { id: 'dashboard', name: 'Executive Dashboard', icon: ChartBarIcon },
      { id: 'crm_leads', name: 'CRM Leads & Pipeline', icon: UserGroupIcon },
      { id: 'customers', name: 'Customer Management', icon: UserGroupIcon },
      // { id: 'analytics', name: 'Usage Analytics', icon: ChartPieIcon },
      { id: 'billing', name: 'Billing & Subscriptions', icon: BanknotesIcon },
      // { id: 'notifications', name: 'Notifications', icon: BellIcon },
      { id: 'audit', name: 'Audit Trail', icon: DocumentArrowDownIcon }
    ];

    // Configuration Options
    const industryOptions = [
      { value: 'technology', label: 'Technology' },
      { value: 'financial_services', label: 'Financial Services' },
      { value: 'healthcare', label: 'Healthcare' },
      { value: 'manufacturing', label: 'Manufacturing' },
      { value: 'retail', label: 'Retail' },
      { value: 'energy', label: 'Energy' },
      { value: 'government', label: 'Government' },
      { value: 'education', label: 'Education' },
      { value: 'consulting', label: 'Consulting' },
      { value: 'other', label: 'Other' }
    ];

    const regionOptions = [
      { value: 'north_america', label: 'North America' },
      { value: 'europe', label: 'Europe' },
      { value: 'asia_pacific', label: 'Asia Pacific' },
      { value: 'latin_america', label: 'Latin America' },
      { value: 'middle_east_africa', label: 'Middle East & Africa' },
      { value: 'global', label: 'Global' }
    ];

    const organizationSizeOptions = [
      { value: 'smb', label: 'Small & Medium Business (1-200)' },
      { value: 'mid', label: 'Mid-Market (201-1000)' },
      { value: 'enterprise', label: 'Enterprise (1000+)' }
    ];

    const subscriptionPlanOptions = [
      { value: 'free', label: 'Free', color: 'gray' },
      { value: 'standard', label: 'Standard', color: 'blue' },
      { value: 'premium', label: 'Premium', color: 'purple' },
      { value: 'enterprise', label: 'Enterprise', color: 'emerald' }
    ];

    const statusOptions = [
      { value: 'active', label: 'Active', color: 'green' },
      { value: 'pending', label: 'Pending', color: 'yellow' },
      { value: 'suspended', label: 'Suspended', color: 'red' },
      { value: 'deleted', label: 'Deleted', color: 'gray' }
    ];
      useEffect(() => {
  if (selectedCustomer && showViewDetailsModal) {
    handleTabClick("Default"); // load Default data first
  }
}, [selectedCustomer, showViewDetailsModal]);
    

useEffect(() => {
  const loadEnabledModules = async () => {
    try {
      const enabled = await fetchEnabledModulesApi(BACKEND_URL);
      setEnabledModules(enabled);
    } catch (error) {
      console.error("Error loading enabled modules:", error);
    }
  };

  loadEnabledModules();
}, []);

// Load top customers when Usage Analytics tab is opened
useEffect(() => {
  if (activeTab === 'analytics') {
    loadTopCustomers();
  }
}, [activeTab]);

const loadTopCustomers = async () => {
  setLoading(true);
  try {
    const topCustomersList = await fetchTopCustomersApi(BACKEND_URL);

    if (topCustomersList.length > 0) {
      setTopCustomers(topCustomersList);

      // If superadmin, load top 5 modules across all customers
      // If regular user, load first company's module details
      if (user?.role === 'admin') {
        await fetchSuperAdminModules();
      } else {
        await fetchEnabledModulesForCompany(user.id, user.company_name);
      }
      
      // Load usage statistics
      await fetchUsageStatistics();
    }
  } catch (error) {
    console.error("Error loading top customers:", error);
    showToast.error("Failed to load top customers");
  } finally {
    setLoading(false);
  }
};

const fetchSuperAdminModules = async () => {
  try {
    const data = await fetchSuperAdminModulesApi();

    if (data.modules) {
      setSelectedModules(data.modules);
    } else {
      setSelectedModules([]);
    }

    setSelectedCompany("All Customers");
  } catch (error) {
    console.error("Error loading superadmin modules:", error);
    setSelectedModules([]);
  }
};


const fetchEnabledModulesForCompany = async (userId, companyName) => {
  try {
    const data = await fetchCompanyModuleUsageApi(userId);

    if (data.modules) {
      const moduleNames = data.modules.map((m) => m.name);
      setSelectedModules(moduleNames);
    } else {
      setSelectedModules([]);
    }

    setSelectedCompany(companyName);
  } catch (error) {
    console.error("Error loading company modules:", error);
    setSelectedModules([]);
  }
};

const fetchUsageStatistics = async () => {
  try {
    const data = await fetchUsageStatisticsApi();
    if (data) {
      setUsageStats(data);
    }
  } catch (error) {
    console.error("Error loading usage statistics:", error);
  }
};

useEffect(() => {
  loadCategories();
}, []);

const loadCategories = async () => {
  const list = await fetchCategoriesApi(BACKEND_URL);
  setCategories(list);
};

useEffect(() => {
  loadEmployeeConfig();
}, []);

const loadEmployeeConfig = async () => {
  try {
    const data = await fetchConfigSettingsApi();
    
    const crmConfigs = data.filter(
      (item) => item.type?.toLowerCase() === "crm"
    );
    
    setEmployeeConfig(crmConfigs);
  } catch (error) {
    console.error("Config error:", error);
    setEmployeeConfig([]);
  } finally {
    setInitialLoading(false);
  }
};

const handleCompanySizeChange = (selectedSize) => {
  const selectedConfig = employeeConfig.find(config => config.name === selectedSize);
  setCustomerFormData({
    ...customerFormData,
    company_size: selectedSize,
    price: selectedConfig ? selectedConfig.value : ''
  });
};


// When a category is clicked, fetch related sub-categories
const handleCategoryClick = async (categoryId) => {
  setSelectedCategory(categoryId);
  setTrustCenterLoading(true);

  try {
    const customerId = selectedCustomer?.id || selectedCustomer?.customer_id;

    const result = await fetchSubCategoriesWithTrustCenterApi(
      categoryId,
      customerId
    );

    setSubSubCategories(result.subcategories);
    setSubCategoryToggles(result.toggles);
  } catch (error) {
    console.error("Failed to load Trust Center data:", error);
    showToast.error("Failed to load Trust Center data");
  } finally {
    setTrustCenterLoading(false);
  }
};

const handleSubCategoryToggle = async (subId) => {
  const prev = subCategoryToggles[subId];
  const updated = !prev;

  // Optimistic UI
  setSubCategoryToggles((p) => ({ ...p, [subId]: updated }));

  try {
    await updateTrustCenterToggleApi(
      selectedCustomer?.id,
      subId,
      updated
    );
   const match = subSubCategories.find((f) => f.id === subId);
   showToast.success(match ? `${match.name} updated successfully`:"Not Found");
  } catch (err) {
    // Rollback
    setSubCategoryToggles((p) => ({ ...p, [subId]: prev }));
    console.error("Failed to update sub-category toggle:", err);
  }
};


    // Load Data on Mount and Tab Change
    useEffect(() => {
      loadDashboardData();
    }, []);

    useEffect(() => {
      switch (activeTab) {
        case 'dashboard':
          loadDashboardData();
          break;
        case 'crm_leads':
          // reset to first page when opening CRM Leads tab and fetch
          setCurrentPage(1);
          loadCRMLeads(1);
          break;
        case 'customers':
          loadCustomers();
          break;
        case 'modules':
          loadAvailableModules();
          break;
        case 'analytics':
          loadUsageAnalytics();
          break;
        case 'billing':
          loadBillingData();
          break;
        case 'audit':
          loadAuditLog();
          break;
        default:
          break;
      }
    }, [activeTab]);

    // When pagination or filters change, reload leads if CRM tab is active
    useEffect(() => {
      if (activeTab === 'crm_leads') {
        loadCRMLeads(currentPage);
      }
    }, [currentPage, leadFilters]);



    // API Functions
const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE_URL}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(options.headers || {})
    },
    ...options
  });

  const contentType = res.headers.get("content-type") || "";
  
  if (!res.ok) {
    // Try to get error details from response
    let errorMessage = `API failed: ${res.status} ${res.statusText}`;
    try {
      if (contentType.includes("application/json")) {
        const errorData = await res.json();
        console.error('API Error Details:', errorData);
        errorMessage = errorData.error || errorData.message || JSON.stringify(errorData);
      }
    } catch (e) {
      console.error('Could not parse error response:', e);
    }
    throw new Error(errorMessage);
  }
  
  if (!contentType.includes("application/json")) {
    throw new Error("Invalid JSON response from server");
  }
  
  return res.json();
};


// ✅ Load data by tab (Default, TrustCenter, AdditionalModule)
const handleTabClick = async (tabName) => {
  try {
    setCustomerActiveTab(tabName);

    const customerId = selectedCustomer?.id || selectedCustomer?.customer_id;
    if (!customerId) return;

    const data = await fetchCustomerRequirementsApi(
      customerId,
      tabName
    );

    if (tabName === "TrustCenter") {
      setTrustCenterModules(data);
    } 
    else if (tabName === "AdditionalModule") {
      const defaults = {
        "AI DSPM": false,
        "AI TPRM": false,
        "AI VAPT": false,
        "AI CSPM": false,
        "AI Virtual SOC": false,
      };
      const specialModules = ["AI-LMS", "AI Asset Manager"];
      const allAdditional = { ...defaults, ...data };

      const movedModules = Object.fromEntries(
        Object.entries(allAdditional).filter(([module]) =>
          specialModules.includes(module)
        )
      );

      const filteredAdditional = Object.fromEntries(
        Object.entries(allAdditional).filter(
          ([module]) => !specialModules.includes(module)
        )
      );

      setAdditionalModules(filteredAdditional);
      if (Object.keys(movedModules).length > 0) {
        setModules((prev) => ({ ...prev, ...movedModules }));
      }
    } 
    else if (tabName === "Default") {
      const specialModules = ["AI-LMS", "AI Asset Manager"];
      const preservedSpecials = Object.fromEntries(
        Object.entries({ ...additionalModules, ...modules }).filter(
          ([module, value]) =>
            specialModules.includes(module) &&
            !Object.prototype.hasOwnProperty.call(data, module)
        )
      );
      setModules({ ...data, ...preservedSpecials });
    }
    else if (tabName === "AIAssetManagement") {
      setAIAssestManagerModules(data);
    }
  } catch (error) {
    console.error("Error loading customer requirements:", error);
  }
};

useEffect(() => {
  if (!selectedCustomer) return;

  // Reset tab to Default
  setCustomerActiveTab("");

  // Reset TrustCenter UI
  setSelectedCategory(null);
  setSubSubCategories([]);
  setSubCategoryToggles({});
  setTrustCenterModules([]);

  // Reset Default modules
  setModules([]);

  // Reset Additional modules
  setAdditionalModules({});
}, [selectedCustomer]);


useEffect(() => {
  if (showViewDetailsModal && selectedCustomer) {
    const customerId = selectedCustomer?.id || selectedCustomer?.customer_id;
    if (customerId) {
      // Always load the current tab data
      handleTabClick(customerActiveTab);

      // 🔹 Ensure Additional Module data refreshes properly on modal open
      if (customerActiveTab === "AdditionalModule") {
        handleTabClick("AdditionalModule");
      }
    }
  }
}, [showViewDetailsModal, customerActiveTab, selectedCustomer]);

// ✅ Update backend when a toggle is changed
const updateCustomerRequirement = async (type, field, value) => {
  const customerId = selectedCustomer?.id || selectedCustomer?.customer_id;
  if (!customerId) return;

  return await updateCustomerRequirementApi(
    BACKEND_URL,
    customerId,
    type,
    field,
    value
  );
};

const handleAdditionalToggle = async (moduleName) => {
  const specialModules = ["AI-LMS", "AI Asset Manager"];
  const isSpecial = specialModules.includes(moduleName);
  const prev = isSpecial ? !!modules[moduleName] : !!additionalModules[moduleName];
  const updated = !prev;

  if (isSpecial) {
    setModules((p) => ({ ...p, [moduleName]: updated }));
  } else {
    setAdditionalModules((p) => ({ ...p, [moduleName]: updated }));
  }

  try {
    const res = await updateAdditionalModuleApi(
      selectedCustomer.id,
      moduleName,
      updated
    );

    if (res?.data?.message) {
      const enabled = await fetchEnabledModulesForSidebarApi(
        selectedCustomer.id
      );

      // localStorage.setItem("enabledModules", JSON.stringify(enabled));
    }
    return true;
  } catch (error) {
    if (isSpecial) {
      setModules((p) => ({ ...p, [moduleName]: prev }));
    } else {
      setAdditionalModules((p) => ({ ...p, [moduleName]: prev }));
    }
    return false;
  }
};

// Toggles one of the two VAPT engagement-type sub-flags stored alongside
// the "AI VAPT" master switch (additionalModules["AI VAPT Access"] =
// {asset_vapt, external_vapt}). Reuses the same generic update_requirement
// endpoint as handleAdditionalToggle above, just with a dict value instead
// of a bool.
const handleVaptAccessToggle = async (key) => {
  const customerId = selectedCustomer?.id || selectedCustomer?.customer_id;
  if (!customerId) return false;

  const prevAccess = additionalModules["AI VAPT Access"] || {
    asset_vapt: false,
    external_vapt: false,
  };
  const updatedAccess = { ...prevAccess, [key]: !prevAccess[key] };
  setAdditionalModules((p) => ({ ...p, "AI VAPT Access": updatedAccess }));

  try {
    await updateAdditionalModuleApi(selectedCustomer.id, "AI VAPT Access", updatedAccess);
    return true;
  } catch (error) {
    setAdditionalModules((p) => ({ ...p, "AI VAPT Access": prevAccess }));
    return false;
  }
};



const loadDashboardData = async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem("token");
    const data = await fetchDashboardStatsApi(BACKEND_URL, token);
    setDashboardData(data);
  } finally {
    setLoading(false);
  }
};

const loadCRMLeads = async (page = currentPage) => {
  setLoading(true);
  try {
    const response = await AiCRMApis.getCustomers({
      page,
      page_size: pageSize,
      search_term: appliedSearchTerm || '',
      filters: Object.fromEntries(
        Object.entries({ ...appliedFilters, ...leadFilters }).filter(([k, v]) => v)
      ),
    });

    // If API returns paginated structure with results and count
    if (response) {
      setCrmLeads(response.results || response.data || response || []);
      // store pagination info if present
      setTotalCount(response.count || response.total || (response.data && response.data.count) || 0);
    } else {
      setCrmLeads([]);
      setTotalCount(0);
    }
  } catch (error) {
    console.error('Error loading CRM leads:', error);
    showToast.error('Failed to load CRM leads');
  } finally {
    setLoading(false);
  }
};

const loadCustomers = async (page = 1) => {
  setCustomersLoading(true);
  setLoadingMoreCustomers(true);
  try {
    const offset = (page - 1) * customerLimit;
    console.log(`Loading customers page ${page}, offset: ${offset}, limit: ${customerLimit}`);
    
    const response = await fetchCustomersApi(customerLimit, offset);
    console.log("Customers API response:", response);
    
    setCustomers(response.admins || []);
    setCustomerTotal(response.total || 0);
    setHasMoreCustomers(response.has_more || false);
    setCustomerPage(page);
  } catch (error) {
    console.error("Failed to load customers:", error);
    showToast.error("Failed to load customers");
  } finally {
    setLoadingMoreCustomers(false);
    setCustomersLoading(false);     // ✅ STOP loader
  }
};


const loadAvailableModules = async () => {
  setLoading(true);
  try {
    const mods = await fetchAvailableModulesApi(apiCall);
    setAvailableModules(mods);
  } finally {
    setLoading(false);
  }
};

    const loadUsageAnalytics = async () => {
      try {
        setLoading(true);
        // Usage analytics would be loaded here
      } catch (error) {
        console.error('Failed to load usage analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    // Load billing data
    const loadBillingData = async () => {
      try {
        setLoading(true);
        const data = await fetchBillingDataApi(apiCall);
        setBillingData(data.billing_data || data || []);
      } catch (error) {
        console.error('Failed to load billing data:', error);
        setBillingData([]);
      } finally {
        setLoading(false);
      }
    };

const loadAuditLog = async (page = 1, append = false) => {
  if (append) {
    setLoadingMoreAuditLogs(true);
  } else {
    setLoading(true);
  }
  
  try {
    // Calculate offset based on page
    const offset = (page - 1) * auditLogLimit;
    
    // Fetch logs with limit and offset
    const data = await AiCRMApis.getCRMLogs(auditLogLimit, offset);
    
    console.log(`📋 [AUDIT LOG] Page ${page}, Offset: ${offset}, Limit: ${auditLogLimit}`);
    console.log(`📋 [AUDIT LOG] Response - Count: ${data.count}, Total: ${data.total}, Has More: ${data.has_more}`);
    
    // Transform CRM logs to match audit log format for display
    const transformedLogs = (data.crm_logs || []).map(log => ({
      log_id: log.log_id,
      action_type: log.action_type,
      action_description: log.description,
      performed_by: log.performed_by,
      customer_id: log.entity_id,
      customer_name: log.entity_name,
      timestamp: log.action_timestamp,
      module_type: 'CRM',
      old_value: log.old_value,
      new_value: log.new_value,
      metadata: {
        ...log.metadata,
        field_changed: log.field_changed,
        old_value: log.old_value,
        new_value: log.new_value,
        entity_type: log.entity_type
      }
    }));
    
    // Use has_more and total from backend response
    setHasMoreAuditLogs(data.has_more || false);
    setAuditLogTotal(data.total || 0);
    
    if (append) {
      setAuditLog(prev => [...prev, ...transformedLogs]);
    } else {
      setAuditLog(transformedLogs);
    }
    
    console.log(`✅ [AUDIT LOG] Loaded ${transformedLogs.length} logs, Total: ${data.total}, Has More: ${data.has_more}`);
  } catch (error) {
    console.error('❌ [ERROR] Exception loading CRM logs:', error);
    showToast.error('Failed to load CRM activity logs');
    if (!append) {
      setAuditLog([]);
    }
  } finally {
    if (append) {
      setLoadingMoreAuditLogs(false);
    } else {
      setLoading(false);
    }
  }
};

// Reset audit log pagination when tab changes
useEffect(() => {
  if (activeTab === 'audit') {
    setAuditLogPage(1);
    setHasMoreAuditLogs(true);
    loadAuditLog(1, false);
  }
}, [activeTab]);

    // Action Functions
const createCustomer = async () => {
  setLoading(true);
  try {
    // Create CRM lead
    const leadData = {
      customer_id: `LEAD-${Date.now()}`,
      company_name: customerFormData.company_name,
      industry: customerFormData.industry,
      company_size: customerFormData.company_size,
      price: customerFormData.price,
      primary_contact_name: customerFormData.primary_contact_name,
      primary_contact_info: {
        email: customerFormData.primary_contact_email,
        phone: customerFormData.primary_contact_phone,
      },
      primary_contact_title: customerFormData.primary_contact_title,
      status: customerFormData.status,
      lead_source: customerFormData.lead_source,
      conversion_probability: customerFormData.conversion_probability,
      tags: customerFormData.tags || []
    };

    const response = await AiCRMApis.createCustomer(leadData);

    showToast.success(`CRM Lead "${customerFormData.company_name}" created successfully!`);
    await loadCRMLeads();
    setShowCreateCustomerModal(false);
    resetCustomerForm();
  } catch (error) {
    showToast.error(`Failed to create lead: ${error.message}`);
    console.error('Error creating lead:', error);
  } finally {
    setLoading(false);
  }
};

const updateCustomer = async () => {
  setLoading(true);
  try {
    // Check if we're updating a lead (has customer_id from CRM) or a customer (has id from user_master)
    const isLead = selectedLead && selectedLead.customer_id;
    
    if (isLead) {
      // Update CRM Lead
      const leadData = {
        company_name: customerFormData.company_name,
        industry: customerFormData.industry,
        company_size: customerFormData.company_size,
        price: customerFormData.price,
        primary_contact_name: customerFormData.primary_contact_name,
        primary_contact_info: {
          email: customerFormData.primary_contact_email,
          phone: customerFormData.primary_contact_phone,
        },
        primary_contact_title: customerFormData.primary_contact_title,
        status: customerFormData.status,
        lead_source: customerFormData.lead_source,
        conversion_probability: customerFormData.conversion_probability,
        notes: customerFormData.notes,
        tags: customerFormData.tags || []
      };

      await AiCRMApis.patchCustomer(selectedLead.customer_id, leadData);

      showToast.success(`Lead "${customerFormData.company_name}" updated successfully!`);
      await loadCRMLeads();
      
      // Refresh audit logs to show the update
      if (activeTab === 'audit') {
        await loadAuditLog();
      }
    } else {
      // Update Customer (from user_master)
      await updateCustomerApi(
        apiCall,
        selectedCustomer.customer_id,
        customerFormData
      );
      await loadCustomers();
    }

    setShowEditCustomerModal(false);
    setSelectedCustomer(null);
    setSelectedLead(null);
    resetCustomerForm();
  } catch (error) {
    showToast.error(`Failed to update: ${error.message}`);
    console.error('Error updating:', error);
  } finally {
    setLoading(false);
  }
};


// const updateCustomerStatus = async (customerId, newStatus, reason) => {
//   setLoading(true);
//   try {
//     await updateCustomerStatusApi(
//       apiCall,
//       customerId,
//       newStatus,
//       reason
//     );

//     await loadCustomers();
//   } finally {
//     setLoading(false);
//   }
// };

const addFollowUp = async () => {
  if (!selectedLead) {
    showToast.error('No lead selected');
    return;
  }

  if (!followUpFormData.scheduled_date) {
    showToast.error('Please select a scheduled date and time');
    return;
  }
  
  setLoading(true);
  try {
    // Convert datetime-local format to ISO 8601
    const formattedData = {
      scheduled_date: new Date(followUpFormData.scheduled_date).toISOString(),
      follow_up_type: followUpFormData.follow_up_type || 'Call',
      notes: followUpFormData.notes || '',
      assigned_to: followUpFormData.assigned_to || '',
      next_action: followUpFormData.next_action || ''
    };
    
    const response = await AiCRMApis.addFollowUp(selectedLead.customer_id, formattedData);
    
    showToast.success('Follow-up scheduled successfully!');
    
    // Refresh leads and close modal
    await loadCRMLeads();
    setShowFollowUpModal(false);
    setSelectedLead(null);
    
    // Reset form
    setFollowUpFormData({
      scheduled_date: '',
      follow_up_type: 'Call',
      notes: '',
      assigned_to: '',
      outcome: '',
      next_action: ''
    });
  } catch (error) {
    console.error('Full error object:', error);
    showToast.error(`Failed to add follow-up: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

const handleEditFollowUp = (followUp, index) => {
  setEditingFollowUpIndex(index);
  
  // Convert scheduled_date to local datetime-local format without timezone conversion
  let scheduledDateLocal = '';
  if (followUp.scheduled_date) {
    const date = new Date(followUp.scheduled_date);
    // Format as YYYY-MM-DDTHH:MM for datetime-local input
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    scheduledDateLocal = `${year}-${month}-${day}T${hours}:${minutes}`;
  }
  
  setFollowUpFormData({
    scheduled_date: scheduledDateLocal,
    follow_up_type: followUp.follow_up_type || 'Call',
    notes: followUp.notes || '',
    assigned_to: followUp.assigned_to || '',
    outcome: followUp.outcome || '',
    next_action: followUp.next_action || '',
    status: followUp.status || 'Scheduled'
  });
  setShowLeadDetailsModal(false);
  setShowFollowUpModal(true);
};

const handleUpdateFollowUp = async (e) => {
  e.preventDefault();
  setLoading(true);

  try {
    if (!selectedLead) {
      showToast.error('No lead selected');
      return;
    }

    if (!followUpFormData.scheduled_date) {
      showToast.error('Please select a scheduled date');
      setLoading(false);
      return;
    }

    // Get the follow_up_id from the existing follow-up
    const followUpToUpdate = selectedLead.follow_ups[editingFollowUpIndex];
    if (!followUpToUpdate || !followUpToUpdate.follow_up_id) {
      showToast.error('Follow-up ID not found');
      setLoading(false);
      return;
    }

    // Build update payload with only changed fields
    const updatePayload = {
      follow_up_id: followUpToUpdate.follow_up_id
    };

    // Check each field and only include if changed
    if (followUpFormData.status !== followUpToUpdate.status) {
      updatePayload.status = followUpFormData.status || 'Scheduled';
    }

    if (followUpFormData.follow_up_type !== followUpToUpdate.follow_up_type) {
      updatePayload.follow_up_type = followUpFormData.follow_up_type || 'Call';
    }

    if (followUpFormData.notes !== (followUpToUpdate.notes || '')) {
      updatePayload.notes = followUpFormData.notes || '';
    }

    if (followUpFormData.assigned_to !== (followUpToUpdate.assigned_to || '')) {
      updatePayload.assigned_to = followUpFormData.assigned_to || '';
    }

    if (followUpFormData.outcome !== (followUpToUpdate.outcome || '')) {
      updatePayload.outcome = followUpFormData.outcome || '';
    }

    if (followUpFormData.next_action !== (followUpToUpdate.next_action || '')) {
      updatePayload.next_action = followUpFormData.next_action || '';
    }

    // Check if scheduled_date changed (compare the datetime-local format)
    const existingDateLocal = followUpToUpdate.scheduled_date 
      ? (() => {
          const date = new Date(followUpToUpdate.scheduled_date);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          return `${year}-${month}-${day}T${hours}:${minutes}`;
        })()
      : '';

    if (followUpFormData.scheduled_date !== existingDateLocal) {
      updatePayload.scheduled_date = new Date(followUpFormData.scheduled_date).toISOString();
    }

    // Call the dedicated update_follow_up endpoint with only changed fields
    await AiCRMApis.updateFollowUp(selectedLead.customer_id, updatePayload);

    showToast.success('Follow-up updated successfully!');
    await loadCRMLeads();
    setShowFollowUpModal(false);
    setEditingFollowUpIndex(null);
    setFollowUpFormData({
      scheduled_date: '',
      follow_up_type: 'Call',
      notes: '',
      assigned_to: '',
      outcome: '',
      next_action: '',
      status: 'Scheduled'
    });
  } catch (error) {
    console.error('Error updating follow-up:', error);
    showToast.error(`Failed to update follow-up: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

const updateLeadStatus = async (customerId, newStatus) => {
  setLoading(true);
  try {
    const response = await AiCRMApis.patchCustomer(customerId, { status: newStatus });
    
    showToast.success(`Lead status updated to "${newStatus}"`);
    await loadCRMLeads();
  } catch (error) {
    console.error('Error updating lead status:', error);
    showToast.error(`Failed to update status: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

const handleConvertToCustomer = async (e) => {
  e.preventDefault();
  setLoading(true);
  
  try {
    // Validate required fields
    const missing = [];
    if (!customerAdminFormData.firstname) missing.push('First Name');
    if (!customerAdminFormData.email) missing.push('Email');
    if (!customerAdminFormData.logoImg) missing.push('Company Logo');
    if (missing.length) {
      showToast.error(`Please fill in required fields: ${missing.join(', ')}`);
      setErrors({
        firstname: !customerAdminFormData.firstname ? 'Required' : undefined,
        email: !customerAdminFormData.email ? 'Required' : undefined,
        logoImg: !customerAdminFormData.logoImg ? 'Required' : undefined,
      });
      setLoading(false);
      return;
    }

    // Create customer admin user
    const renewalConfig = customerConfig.find(config => config.name === 'renewal');
    const userResponse = await saveUserApi({
      firstname: customerAdminFormData.firstname,
      lastname: customerAdminFormData.lastname,
      email: customerAdminFormData.email,
      phone: customerAdminFormData.phone,
      role: 'customer_admin',
      customername: customerAdminFormData.customername,
      location: customerAdminFormData.location,
      department: customerAdminFormData.department,
      employeecount: customerAdminFormData.employeecount,
      price: customerAdminFormData.price,
      logoImg: customerAdminFormData.logoImg,
      renewal: renewalConfig?.value || 12,
      status: 'active',
      created_by: user?.email || 'system',
      updated_by: user?.email || 'system'
    });

    // Update lead status to Confirmed
    await AiCRMApis.patchCustomer(selectedLead.customer_id, { 
      status: 'Confirmed',
      converted_to_user: true,
      conversion_date: new Date().toISOString(),
      converted_user_id: userResponse.id
    });

    showToast.success(`Customer admin created successfully! ${customerAdminFormData.customername} is now a confirmed customer.`);
    
    // Reset and close modals
    setShowConvertToCustomerModal(false);
    setSelectedLead(null);
    setCustomerAdminFormData({
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      customername: '',
      location: '',
      department: '',
      employeecount: '',
      price: '',
      logoImg: null
    });
    setErrors({});
    
    // Reload data
    await loadCRMLeads();
    await loadCustomers();
    
  } catch (error) {
    console.error('Error converting to customer:', error);
    showToast.error(`Conversion failed: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

// Handle Customer Renewal
const handleRenewal = async (e) => {
  e.preventDefault();
  setLoading(true);
  
  try {
    if (!renewalCustomer) {
      showToast.error('No customer selected for renewal');
      return;
    }

    // Validate fields
    if (!renewalFormData.employeecount || !renewalFormData.price) {
      showToast.error('Please fill in all required fields');
      setLoading(false);
      return;
    }

    // Use selected renewal date from DatePicker, or calculate 1 year ahead if not provided
    let newRenewalDate;
    if (renewalFormData.renewal_date) {
      newRenewalDate = renewalFormData.renewal_date; // Already in YYYY-MM-DD format from DatePicker
    } else {
      const currentRenewalDate = new Date(renewalCustomer.renewal_date);
      const calculatedDate = new Date(currentRenewalDate);
      calculatedDate.setFullYear(calculatedDate.getFullYear() + 1);
      newRenewalDate = calculatedDate.toISOString().split('T')[0];
    }

    // Use the MongoDB user id from renewalCustomer
    const userId = renewalCustomer.id;
    
    if (!userId) {
      showToast.error('Unable to renew: Customer ID not found. Please refresh and try again.');
      return;
    }
    
    // Call renewal API from service file
    await renewCustomerSubscriptionApi(userId, {
      employeecount: renewalFormData.employeecount,
      price: renewalFormData.price,
      renewal_date: newRenewalDate,
      updated_by: user?.email || 'system'
    });

    showToast.success(`Successfully renewed subscription for ${renewalCustomer.company}!`);
    
    // Reset and close modal
    setShowRenewalModal(false);
    setRenewalCustomer(null);
    setRenewalFormData({
      employeecount: '',
      price: '',
      renewal_date: ''
    });
    
    // Reload data
    await loadCustomers();
    await loadBillingData();
    
    // Reload audit logs to show the renewal in audit trail
    if (activeTab === 'audit') {
      await loadAuditLog();
    }
    
  } catch (error) {
    console.error('Error processing renewal:', error);
    showToast.error(`Renewal failed: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

// Load renewal history for a customer
const loadRenewalHistory = async (customerId) => {
  setLoading(true);
  try {
    console.log('📋 [RENEWAL HISTORY] Loading history for customer ID:', customerId);
    const response = await getCustomerRenewalHistoryApi(customerId);
    
    console.log('📋 [RENEWAL HISTORY] Response:', response);
    
    if (response.success) {
      setRenewalHistory(response.renewal_history || []);
      console.log(`✅ [RENEWAL HISTORY] Loaded ${response.total_renewals} renewal records`);
    } else {
      showToast.error('Failed to load renewal history');
      setRenewalHistory([]);
    }
  } catch (error) {
    console.error('❌ [RENEWAL HISTORY ERROR]:', error);
    showToast.error(`Failed to load renewal history: ${error.message}`);
    setRenewalHistory([]);
  } finally {
    setLoading(false);
  }
};

// Handle viewing renewal history
const handleViewRenewalHistory = async (customer) => {
  console.log('🔍 [RENEWAL HISTORY] Selected customer:', customer);
  setSelectedCustomerForHistory(customer);
  setShowRenewalHistoryModal(true);
  await loadRenewalHistory(customer.id);
};


    {/*const handleTabClick = async (tabName) => {
      try {
        setActiveTab(tabName);
        
        // Assuming you have selectedCustomer already set
        const customerId = selectedCustomer?.id || selectedCustomer?.customer_id;
        if (!customerId) {
          console.warn("No customer selected.");
          return;
        }

        const response = await fetch(`${BACKEND_URL}/api/customer-requirements/${customerId}/?type=${tabName}`);
        if (!response.ok) {
          throw new Error(`Failed to load data: ${response.statusText}`);
        }

        const data = await response.json();
        setModules(data.data || {});
      } catch (error) {
        console.error("Error loading customer requirements:", error);
      }
    }; */}


    // Utility Functions
    const resetCustomerForm = () => {
      setCustomerFormData({
        company_name: '',
        industry: 'technology',
        company_size: employeeConfig.length > 0 ? employeeConfig[0].name : '',
        price: '',
        primary_contact_name: '',
        primary_contact_email: '',
        primary_contact_phone: '',
        primary_contact_title: '',
        lead_source: '',
        status: 'New Lead',
        notes: '',
        tags: [],
        conversion_probability: 0
      });
    };

    const getStatusColor = (status) => {
      const colors = {
        'active': 'bg-green-100 text-green-800',
        'pending': 'bg-yellow-100 text-yellow-800',
        'suspended': 'bg-red-100 text-red-800',
        'deleted': 'bg-gray-100 text-gray-800'
      };
      return colors[status?.toLowerCase()] || colors.active;
    };

    const getSubscriptionColor = (plan) => {
      const colors = {
        'free': 'bg-gray-100 text-gray-800',
        'standard': 'bg-blue-100 text-blue-800',
        'premium': 'bg-purple-100 text-purple-800',
        'enterprise': 'bg-emerald-100 text-emerald-800'
      };
      return colors[plan?.toLowerCase()] || colors.free;
    };

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    // Render Module Management Tab
    const renderModuleManagement = () => (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-bold flex items-center mb-6">
            <CogIcon className="w-6 h-6 text-blue-500 mr-2" />
            Module Management
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(availableModules).map(([moduleKey, moduleInfo]) => (
              <div key={moduleKey} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{moduleInfo.name}</h3>
                    <p className="text-sm opacity-80 mt-1">{moduleInfo.description}</p>
                  </div>
                  <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                    {moduleInfo.category}
                  </span>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Available in Plans:</h4>
                    <div className="flex flex-wrap gap-1">
                      {moduleInfo.available_in_plans?.map((plan) => (
                        <span key={plan} className={`px-2 py-1 text-xs rounded ${getSubscriptionColor(plan)}`}>
                          {plan.toUpperCase()}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Key Features:</h4>
                    <ul className="text-xs text-gray-600 space-y-1">
                      {moduleInfo.features?.slice(0, 3).map((feature, index) => (
                        <li key={index}>• {feature}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );

    // Render Enhanced Usage Analytics Tab
    const renderUsageAnalytics = () => {
      if (loading) {
        return (
          <ApiLoader label="Loading usage analytics..." className="h-96" />
        );
      }

      return (
      <div className="space-y-6">
        {/* Overall Usage Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {usageStats ? [
            {
              title: 'Total Platform Usage',
              value: usageStats.total_platform_usage?.value || '0',
              unit: usageStats.total_platform_usage?.unit || 'API Calls/Month',
              icon: ServerIcon,
              color: 'blue',
              trend: usageStats.total_platform_usage?.trend || '+0%'
            },
            {
              title: 'Active Customer Sessions',
              value: usageStats.active_customer_sessions?.value || '0',
              unit: usageStats.active_customer_sessions?.unit || 'Current Sessions',
              icon: UserGroupIcon,
              color: 'green',
              trend: usageStats.active_customer_sessions?.trend || '+0%'
            },
            {
              title: 'Module Adoption Rate',
              value: usageStats.module_adoption_rate?.value || '0.0',
              unit: usageStats.module_adoption_rate?.unit || 'Avg per Customer',
              icon: CogIcon,
              color: 'purple',
              trend: usageStats.module_adoption_rate?.trend || '+0%'
            },
            {
              title: 'Customer Satisfaction',
              value: usageStats.customer_satisfaction?.value || '0.0/5',
              unit: usageStats.customer_satisfaction?.unit || 'NPS Score',
              icon: CheckCircleIcon,
              color: 'emerald',
              trend: usageStats.customer_satisfaction?.trend || '+0.0'
            }
          ].map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{stat.title}</h3>
                <stat.icon className={`w-6 h-6 text-${stat.color}-500`} />
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{stat.value}</div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">{stat.unit}</div>
              <div className={`text-xs font-medium text-${stat.color}-600 dark:text-${stat.color}-400`}>
                {stat.trend} from last month
              </div>
            </motion.div>
          )) : (
            // Loading placeholders
            [1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
              </div>
            ))
          )}
        </div>

        {/* Customer Usage Insights */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Performing Customers */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">Top Performing Customers</h3>
            <div className="space-y-4">
              {topCustomers.length > 0 ? (
                topCustomers.map((customer, index) => {
                  // Extract numeric usage percentage
                  const usagePercent = parseInt(customer.usage) || 0;
                  
                  return (
                    <div
                      key={index}
                      className="p-4 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:shadow-md dark:hover:bg-gray-600 transition-all"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4
                            className="font-medium text-blue-600 dark:text-blue-400 cursor-pointer hover:underline"
                            onClick={() => fetchEnabledModulesForCompany(customer.user_id, customer.company_name)}
                          >
                            {customer.company_name}
                          </h4>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              {customer.used_count} active users
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                            {customer.revenue}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-300">Yearly Revenue</div>
                        </div>
                      </div>
                      
                      {/* Usage Progress Bar */}
                      <div className="mt-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Usage</span>
                          <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">{customer.usage}</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${usagePercent}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-sm">No top customers found.</p>
              )}
            </div>
          </div>

          {/* Module Usage Analytics */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">
              {selectedCompany
                ? `Module Usage Insights — ${selectedCompany}`
                : "Module Usage Insights"}
            </h3>

            <div className="space-y-3">
              {selectedModules.length > 0 ? (
                selectedModules.map((module, index) => {
                  // Check if it's superadmin data (has enabled_count) or customer data (string)
                  const isString = typeof module === 'string';
                  const moduleName = isString ? module : module.name;
                  const enabledCount = isString ? null : module.enabled_count;

                  return (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-all shadow-sm">
                      <div className="flex items-center space-x-3">
                        <div className="w-2.5 h-2.5 bg-green-500 dark:bg-green-400 rounded-full shadow-sm"></div>
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white">{moduleName}</h4>
                          <div className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                            {enabledCount ? `Used by ${enabledCount} customer${enabledCount > 1 ? 's' : ''}` : 'Enabled'}
                          </div>
                        </div>
                      </div>
                      {/* {enabledCount && (
                        <div className="flex items-center space-x-2">
                          <div className="text-right">
                            <div className="text-2xl font-bold text-gray-900 dark:text-white">{enabledCount} </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Customers</div>
                          </div>
                        </div>
                      )} */}
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    {selectedCompany && selectedCompany !== "All Customers"
                      ? "No enabled modules found for this company."
                      : user?.role === 'admin' 
                        ? "Loading top modules across all customers..."
                        : "Click a company name above to view its enabled modules."}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>


        {/* Customer Health Scores */}
        {/* <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Customer Health & Risk Analysis</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-3xl font-bold text-green-600 mb-2">78%</div>
              <div className="text-sm font-medium text-green-800">Healthy Customers</div>
              <div className="text-xs text-green-600">High engagement & usage</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-3xl font-bold text-yellow-600 mb-2">15%</div>
              <div className="text-sm font-medium text-yellow-800">At-Risk Customers</div>
              <div className="text-xs text-yellow-600">Declining usage patterns</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-3xl font-bold text-red-600 mb-2">7%</div>
              <div className="text-sm font-medium text-red-800">High Churn Risk</div>
              <div className="text-xs text-red-600">Require immediate attention</div>
            </div>
          </div>
        </div> */}
      </div>
    );

    // Render Notifications Tab
    const renderNotifications = () => (
      <div className="space-y-6">
        {/* Notification Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            {
              title: 'Total Notifications Sent',
              value: '12,847',
              subtitle: 'This Month',
              icon: EnvelopeIcon,
              color: 'blue'
            },
            {
              title: 'Email Delivery Rate',
              value: '98.7%',
              subtitle: 'Successful Deliveries',
              icon: CheckCircleIcon,
              color: 'green'
            },
            {
              title: 'Customer Engagement',
              value: '74.2%',
              subtitle: 'Open Rate',
              icon: EyeIcon,
              color: 'purple'
            },
            {
              title: 'Failed Deliveries',
              value: '167',
              subtitle: 'Require Attention',
              icon: XCircleIcon,
              color: 'red'
            }
          ].map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{stat.title}</h3>
                <stat.icon className={`w-6 h-6 text-${stat.color}-500`} />
              </div>
              <div className="text-2xl font-bold mb-1">{stat.value}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{stat.subtitle}</div>
            </motion.div>
          ))}
        </div>

        {/* Recent Notifications */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-gray-900">Recent Notifications</h3>
            <button className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              <PlusIcon className="w-5 h-5" />
              <span>Send Notification</span>
            </button>
          </div>

          <div className="space-y-4">
            {[
              {
                type: 'welcome',
                subject: 'Welcome to Tanikkai Platform',
                recipient: 'New Customer: TechCorp Solutions',
                status: 'delivered',
                timestamp: '2024-11-28 10:30 AM',
                engagement: 'opened'
              },
              {
                type: 'subscription_changed',
                subject: 'Subscription Plan Updated',
                recipient: 'Global Manufacturing Inc',
                status: 'delivered',
                timestamp: '2024-11-28 09:15 AM',
                engagement: 'opened'
              },
              {
                type: 'account_suspended',
                subject: 'Account Temporarily Suspended',
                recipient: 'Inactive Customer Co',
                status: 'bounced',
                timestamp: '2024-11-28 08:45 AM',
                engagement: 'bounced'
              },
              {
                type: 'modules_updated',
                subject: 'New Modules Available',
                recipient: 'Healthcare Systems Ltd',
                status: 'delivered',
                timestamp: '2024-11-27 04:20 PM',
                engagement: 'clicked'
              },
              {
                type: 'usage_warning',
                subject: 'Usage Limit Approaching',
                recipient: 'Energy Solutions LLC',
                status: 'delivered',
                timestamp: '2024-11-27 02:10 PM',
                engagement: 'opened'
              }
            ].map((notification, index) => (
              <div key={index} className="flex items-center justify-between p-4 border border-gray-100 rounded-lg hover:bg-gray-50">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      notification.type === 'welcome' ? 'bg-green-100 text-green-800' :
                      notification.type === 'subscription_changed' ? 'bg-blue-100 text-blue-800' :
                      notification.type === 'account_suspended' ? 'bg-red-100 text-red-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {notification.type.replace('_', ' ').toUpperCase()}
                    </span>
                    <h4 className="font-medium text-gray-900">{notification.subject}</h4>
                  </div>
                  <div className="text-sm opacity-80 mb-1">{notification.recipient}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{notification.timestamp}</div>
                </div>
                <div className="flex items-center space-x-4">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    notification.status === 'delivered' ? 'bg-green-100 text-green-800' :
                    notification.status === 'bounced' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {notification.status.toUpperCase()}
                  </span>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    notification.engagement === 'clicked' ? 'bg-green-100 text-green-800' :
                    notification.engagement === 'opened' ? 'bg-blue-100 text-blue-800' :
                    notification.engagement === 'bounced' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {notification.engagement?.toUpperCase() || 'PENDING'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Main component return
  if (initialLoading) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-64">
          <PageLoader title="Loading AI CRM..." minH="min-h-screen" />
        </div>
      </div>
    );
  }

  return (
    <>
        <div className="p-3 sm:p-4 md:p-6 max-w-7xl mx-auto bg-white dark:bg-[#0f172a] min-h-screen">
          {/* Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-4 md:mb-6 gap-4">
              <div className="flex items-center space-x-4">
                <div>
                  <BuildingOfficeIcon className="w-6 md:w-8 h-6 md:h-8 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-xl md:text-3xl font-bold text-gray-900 dark:text-white truncate">AI CRM</h1>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 truncate md:truncate-none">AI-powered customer relationship management system</p>
                </div>
              </div>
              
              <div className="hidden sm:flex items-center space-x-3 text-xs md:text-sm text-gray-600 dark:text-gray-400">
                <span>Platform Admin:</span>
                <strong className="text-gray-900 dark:text-white">{user?.email}</strong>
              </div>
            </div>

            {/* Navigation Tabs */}
            <TabNavigation
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            {activeTab === 'dashboard' && (
              <motion.div key="dashboard" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <DashboardTab 
                  loading={loading}
                  dashboardData={dashboardData}
                  isDarkMode={isDarkMode}
                  formatDate={formatDate}
                />
              </motion.div>
            )}
            {activeTab === 'crm_leads' && (
              <motion.div key="crm_leads" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <CRMLeadsTab 
                  loading={loading}
                  crmLeads={crmLeads}
                  currentPage={currentPage}
                  totalCount={totalCount}
                  pageSize={pageSize}
                  isDarkMode={isDarkMode}
                  formatDate={formatDate}
                  setShowCreateCustomerModal={setShowCreateCustomerModal}
                  updateLeadStatus={updateLeadStatus}
                  setSelectedLead={setSelectedLead}
                  setShowLeadDetailsModal={setShowLeadDetailsModal}
                  setCurrentPage={setCurrentPage}
                  leadFilters={leadFilters}
                  setLeadFilters={setLeadFilters}
                />
              </motion.div>
            )}
            {activeTab === 'customers' && (
              <motion.div key="customers" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <CustomersTab 
                  loading={customersLoading} 
                  customers={customers}
                  appliedSearchTerm={appliedSearchTerm}
                  appliedFilters={appliedFilters}
                  statusOptions={statusOptions}
                  industryOptions={industryOptions}
                  setSelectedCustomer={setSelectedCustomer}
                  setShowViewDetailsModal={setShowViewDetailsModal}
                  setRenewalCustomer={setRenewalCustomer}
                  setRenewalFormData={setRenewalFormData}
                  setShowRenewalModal={setShowRenewalModal}
                  handleViewRenewalHistory={handleViewRenewalHistory}
                  customerPage={customerPage}
                  setCustomerPage={setCustomerPage}
                  customerLimit={customerLimit}
                  customerTotal={customerTotal}
                  hasMoreCustomers={hasMoreCustomers}
                  loadingMoreCustomers={loadingMoreCustomers}
                  loadCustomers={loadCustomers}
                  onApplyFilters={(search, filters) => {
                    setAppliedSearchTerm(search);
                    setAppliedFilters(filters);
                    setSearchTerm(search);
                    setFilters(filters);
                  }}
                  onResetFilters={() => {
                    const emptyFilters = { status: '', subscription_plan: '', industry: '', region: '', organization_size: '' };
                    setSearchTerm('');
                    setFilters(emptyFilters);
                    setAppliedSearchTerm('');
                    setAppliedFilters(emptyFilters);
                  }}
                />
              </motion.div>
            )}
            {activeTab === 'modules' && (
              <motion.div key="modules" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                {renderModuleManagement()}
              </motion.div>
            )}
            {activeTab === 'analytics' && (
              <motion.div key="analytics" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                {renderUsageAnalytics()}
              </motion.div>
            )}
            {activeTab === 'billing' && (
              <motion.div key="billing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <BillingSubscriptionsTab 
                  loading={loading}
                  billingData={billingData}
                />
              </motion.div>
            )}
            {activeTab === 'audit' && (
              <motion.div key="audit" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <AuditLogTab 
                  loading={loading}
                  auditLog={auditLog}
                  loadAuditLog={loadAuditLog}
                  formatDate={formatDate}
                  auditLogPage={auditLogPage}
                  setAuditLogPage={setAuditLogPage}
                  auditLogLimit={auditLogLimit}
                  auditLogTotal={auditLogTotal}
                  hasMoreAuditLogs={hasMoreAuditLogs}
                  loadingMoreAuditLogs={loadingMoreAuditLogs}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Customer Details Modal */}
        <AnimatePresence>
          <CustomerDetailsModal
            show={showViewDetailsModal}
            onClose={() => setShowViewDetailsModal(false)}
            selectedCustomer={selectedCustomer}
            customerActiveTab={customerActiveTab}
            handleTabClick={handleTabClick}
            categories={categories}
            selectedCategory={selectedCategory}
            handleCategoryClick={handleCategoryClick}
            subSubCategories={subSubCategories}
            subCategoryToggles={subCategoryToggles}
            handleSubCategoryToggle={handleSubCategoryToggle}
            modules={modules}
            additionalModules={additionalModules}
            handleAdditionalToggle={handleAdditionalToggle}
            handleVaptAccessToggle={handleVaptAccessToggle}
            setAdditionalModules={setAdditionalModules}
            AIAssestManagerModules={AIAssestManagerModules}
            trustCenterLoading={trustCenterLoading}
          />
        </AnimatePresence>

        {/* Create CRM Lead Modal */}
        <CreateCustomerModal
          show={showCreateCustomerModal}
          onClose={() => setShowCreateCustomerModal(false)}
          loading={loading}
          customerFormData={customerFormData}
          setCustomerFormData={setCustomerFormData}
          industryOptions={industryOptions}
          employeeConfig={employeeConfig}
          handleCompanySizeChange={handleCompanySizeChange}
          createCustomer={createCustomer}
          resetCustomerForm={resetCustomerForm}
        />

        {/* Add Follow-up Modal */}
        <FollowUpModal
          show={showFollowUpModal}
          selectedLead={selectedLead}
          onClose={() => setShowFollowUpModal(false)}
          isDarkMode={isDarkMode}
          editingFollowUpIndex={editingFollowUpIndex}
          followUpFormData={followUpFormData}
          setFollowUpFormData={setFollowUpFormData}
          handleUpdateFollowUp={handleUpdateFollowUp}
          addFollowUp={addFollowUp}
          loading={loading}
          setEditingFollowUpIndex={setEditingFollowUpIndex}
          setSelectedLead={setSelectedLead}
        />

        {/* Renewal Modal */}
        <RenewalModal
          show={showRenewalModal}
          renewalCustomer={renewalCustomer}
          onClose={() => setShowRenewalModal(false)}
          renewalFormData={renewalFormData}
          setRenewalFormData={setRenewalFormData}
          employeeConfig={employeeConfig}
          handleRenewal={handleRenewal}
          loading={loading}
          DatePicker={DatePicker}
          setRenewalCustomer={setRenewalCustomer}
        />

        {/* Edit Lead Modal */}
        <EditCustomerModal
          show={showEditCustomerModal}
          selectedLead={selectedLead}
          onClose={() => setShowEditCustomerModal(false)}
          loading={loading}
          customerFormData={customerFormData}
          setCustomerFormData={setCustomerFormData}
          industryOptions={industryOptions}
          employeeConfig={employeeConfig}
          handleCompanySizeChange={handleCompanySizeChange}
          updateCustomer={updateCustomer}
          resetCustomerForm={resetCustomerForm}
          setSelectedLead={setSelectedLead}
        />

        {/* CRM Lead Details Modal */}
        <LeadDetailsModal
          show={showLeadDetailsModal}
          selectedLead={selectedLead}
          onClose={() => setShowLeadDetailsModal(false)}
          isDarkMode={isDarkMode}
          employeeConfig={employeeConfig}
          formatDate={formatDate}
          setCustomerFormData={setCustomerFormData}
          setShowLeadDetailsModal={setShowLeadDetailsModal}
          setShowEditCustomerModal={setShowEditCustomerModal}
          setShowFollowUpModal={setShowFollowUpModal}
          handleEditFollowUp={handleEditFollowUp}
          setCustomerAdminFormData={setCustomerAdminFormData}
          setShowConvertToCustomerModal={setShowConvertToCustomerModal}
          setSelectedLead={setSelectedLead}
        />

        {/* Convert to Customer - Add Customer Admin Modal */}
        <ConvertToCustomerModal
          show={showConvertToCustomerModal}
          onClose={() => setShowConvertToCustomerModal(false)}
          loading={loading}
          customerAdminFormData={customerAdminFormData}
          setCustomerAdminFormData={setCustomerAdminFormData}
          employeeConfig={employeeConfig}
          handleConvertToCustomer={handleConvertToCustomer}
          errors={errors}
          setErrors={setErrors}
        />

        {/* Renewal History Modal */}
        <RenewalHistoryModal
          show={showRenewalHistoryModal}
          onClose={() => {
            setShowRenewalHistoryModal(false);
            setSelectedCustomerForHistory(null);
            setRenewalHistory([]);
          }}
          customer={selectedCustomerForHistory}
          renewalHistory={renewalHistory}
          loading={loading}
          formatDate={formatDate}
        />

    </>
    );
};

export default AiCRM;
